import React, { useEffect, useState } from "react";
import baseImg from "../../../../assets/images/base.png";
import simpleImg from "../../../../assets/images/simple.png";
import Image from "../../Image";
import InputFile from "../../../form/InputFile";
import Checkbox from "../../../form/Checkbox";
import Input from "../../../form/Input";
import { motion, AnimatePresence, m } from "framer-motion";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useForm from "../../../../hooks/useForm";
import { organizationAddEdit } from "../../../../services/organizationService";
import { toast } from "react-toastify";
import Form from "../../../form/Form";
import Button from "../../../form/Button";
import ThemeStylesEditor from "./ThemeStylesEditor ";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const options = [
  { key: "simple", label: "Simple", image: simpleImg },
  { key: "base", label: "Base", image: baseImg },
];

const initial_style = {
  basicStyle: [
    { key: "background", value: "#F5F3FF" },
    { key: "text", value: "#2C005B" },
  ],
  infoStyle: [
    { key: "background", value: "#F5F5F5" },
    { key: "text", value: "#2C005B" },
  ],
  headerStyle: [
    { key: "background", value: "" },
    { key: "text", value: "#2C005B" },
  ],
  searchBarStyle: [{ key: "background", value: "#F5F5F5" }],
};

const OrganisationContent = ({
  data = {},
  organizationDetailsRefetch,
  selectedLang,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { values, handleChange, handleSubmit, errors, setFieldsValue } =
    useForm({
      initialState: {
        theme: "simple",
        styles: {
          basicStyle: initial_style?.basicStyle,
          infoStyle: initial_style?.infoStyle,
          headerStyle: initial_style?.headerStyle,
          searchBarStyle: initial_style?.searchBarStyle,
          customFonts: [],
        },
        headerLink: [
          { key: "store", isVisible: "", label: "", url: "" },
          { key: "contact", isVisible: "", label: "", url: "" },
          { key: "custom", isVisible: "", label: "", url: "" },
        ],
        logo: null,
        socialMedia: [
          { url: "", name: "Facebook", icon: "fa-brands fa-facebook-f" },
          { url: "", name: "X", icon: "fa-brands fa-x-twitter" },
          { url: "", name: "Instagram", icon: "fa-brands fa-instagram" },
          { url: "", name: "YouTube", icon: "fa-brands fa-youtube" },
        ],
        visibleSocialMedia: false,
      },
    });

  useEffect(() => {
    setFieldsValue({
      theme: data?.themeSetting?.theme,
      styles: {
        basicStyle:
          data?.themeSetting?.styles?.basicStyle || initial_style?.basicStyle,
        infoStyle:
          data?.themeSetting?.styles?.infoStyle || initial_style?.infoStyle,
        headerStyle:
          data?.themeSetting?.styles?.headerStyle || initial_style?.headerStyle,
        searchBarStyle:
          data?.themeSetting?.styles?.searchBarStyle ||
          initial_style?.searchBarStyle,
        customFonts: data?.themeSetting?.styles?.customFonts || [
          { key: "headingFont", value: "Montserrat" },
          { key: "bodyFont", value: "Roboto" },
        ],
      },
      headerLink:
        data?.themeSetting?.headerLink.length > 0
          ? data?.themeSetting?.headerLink?.map((item) => ({
              ...item,
              label: item?.label?.[selectedLang],
            }))
          : [
              {
                key: "store",
                isVisible: true,
                label: "store link",
                url: "",
              },
              {
                key: "contact",
                isVisible: true,
                label: "contact link",
                url: "",
              },
              {
                key: "custom",
                isVisible: true,
                label: "custom link",
                url: "www.customlink.com",
              },
            ],
      logo: data?.themeSetting?.logo,
      socialMedia:
        data?.setting?.socialMedia?.length > 0
          ? data?.setting?.socialMedia
          : [
              { url: "", name: "Facebook", icon: "fa-brands fa-facebook-f" },
              { url: "", name: "X", icon: "fa-brands fa-x-twitter" },
              { url: "", name: "Instagram", icon: "fa-brands fa-instagram" },
              { url: "", name: "YouTube", icon: "fa-brands fa-youtube" },
            ],
      visibleSocialMedia: data?.setting?.visibleSocialMedia,
    });
  }, [data, selectedLang]);

  const { mutate: saveEventTheme, isPending } = useMutation({
    mutationFn: async (value) => {
      const payload = {
        ...value,
        themeSetting: {
          theme: value?.theme,
          styles: value.styles,

          headerLink: value?.headerLink?.map((item, i) => ({
            ...item,
            label: {
              ...data?.themeSetting?.headerLink?.[i]?.label,
              [selectedLang]: item?.label,
            },
            ordering: i,
          })),
          logo: value?.logo,
        },
        setting: {
          socialMedia: value?.socialMedia,
          visibleSocialMedia: value?.visibleSocialMedia,
        },
      };

      delete payload?.theme;
      delete payload?.styles;
      delete payload?.headerLink;
      delete payload?.logo;
      delete payload?.socialMedia;
      delete payload?.visibleSocialMedia;

      const res = await organizationAddEdit({
        ...(data?._id && { id: data?._id, ...payload }),
      });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        organizationDetailsRefetch();
        toast.success(res?.message);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
  });

  function resetStyleHandler({ type }) {
    switch (type) {
      case "basicStyle":
        setFieldsValue({
          styles: { ...values?.styles, basicStyle: initial_style?.basicStyle },
        });
        break;
      case "infoStyle":
        setFieldsValue({
          styles: {
            ...values?.styles,
            infoStyle: initial_style?.infoStyle,
          },
        });
        break;
      case "headerStyle":
        setFieldsValue({
          styles: {
            ...values?.styles,
            headerStyle: initial_style?.headerStyle,
          },
        });
        break;
      case "searchBarStyle":
        setFieldsValue({
          styles: {
            ...values?.styles,
            searchBarStyle: initial_style?.searchBarStyle,
          },
        });
        break;

      default:
        break;
    }
  }

  return (
    <Form onSubmit={saveEventTheme} handleSubmit={handleSubmit}>
      <div className="relative divide-y divide-slate-200 space-y-5">
        <div className="py-4 md:py-8 px-3 md:px-6 rounded-md space-y-4 border border-gray-200">
          <ThemeStylesEditor
            values={values}
            setFieldsValue={setFieldsValue}
            resetStyle={resetStyleHandler}
          />
          <div className="relative py-3">
            <h2 className="text-xl font-bold text-deep-purple mb-2 font-Montserrat">
              {t("Header links")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {values?.headerLink?.map((item) => (
                <div key={item?.key} className="relative">
                  <div className="space-y-3">
                    <Checkbox
                      checkboxLabel={t(`Show ${item?.key} link`)}
                      checkboxLableClass="font-medium text-text-disabled text-lg text-gray-800"
                      checked={item?.isVisible}
                      onChange={(e) => {
                        setFieldsValue({
                          headerLink: values?.headerLink?.map((link) =>
                            link?.key === item?.key
                              ? { ...link, isVisible: e.target.checked }
                              : link
                          ),
                        });
                      }}
                    />

                    <AnimatePresence initial={false}>
                      {item?.isVisible && (
                        <motion.div
                          key="store-input"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.25 }}
                        >
                          <Input
                            inputClasses="!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-gray-800 !pr-10"
                            inputPlaceholder={`${item?.key} link text`}
                            value={item?.label}
                            onChange={(e) => {
                              setFieldsValue({
                                headerLink: values?.headerLink?.map((link) =>
                                  link?.key === item?.key
                                    ? { ...link, label: e.target.value }
                                    : link
                                ),
                              });
                            }}
                          />
                          {/* {item?.key === "custom" && ( */}
                          <Input
                            inputClasses="mt-4 !border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !pr-10"
                            inputPlaceholder={`${item?.key} link URL e.g https://www.example.com`}
                            value={item?.url}
                            onChange={(e) => {
                              setFieldsValue({
                                headerLink: values?.headerLink?.map((link) =>
                                  link?.key === item?.key
                                    ? { ...link, url: e.target.value }
                                    : link
                                ),
                              });
                            }}
                          />
                          {/* // )} */}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative py-3">
            <h2 className="text-xl font-bold text-deep-purple mb-2 font-Montserrat">
              {t("Social media")}
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {/* Social media Link */}
              <div className="relative">
                <div className="space-y-3">
                  <Checkbox
                    checkboxLabel={t("Show social media icons")}
                    checkboxLableClass="font-medium text-text-disabled text-lg text-gray-800"
                    checked={values?.visibleSocialMedia}
                    checkboxName={"visibleSocialMedia"}
                    onChange={handleChange}
                  />

                  <AnimatePresence initial={false}>
                    {values?.visibleSocialMedia && (
                      <motion.div
                        key="store-input"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                      >
                        <div className="space-y-2">
                          {values?.socialMedia?.map((social, i) => (
                            <Input
                              key={i}
                              label={social?.name}
                              value={social?.url}
                              labelClasses={"!text-base !text-gray-800 !mb-1"}
                              inputClasses="!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-gray-800 !pr-10"
                              inputPlaceholder={`https://www.${social?.name?.toLowerCase()}.com/accountname`}
                              onChange={(e) =>
                                setFieldsValue({
                                  socialMedia: values?.socialMedia?.map(
                                    (item) =>
                                      item?.name === social?.name
                                        ? { ...item, url: e.target.value }
                                        : item
                                  ),
                                })
                              }
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
          {/* <div className="relative py-3">
          <h2 className="text-xl font-bold text-deep-purple mb-2 font-Montserrat">
            Accessibility
          </h2>
          <div className="bg-gradient-to-r from-sky-100 to-sky-50 border-l-4 border-sky-400 p-5 rounded-xl shadow flex items-center gap-2">
            <div className="text-sky-500 text-xl mt-0.5">
              <i className="fa-regular fa-circle-question"></i>
            </div>
            <div className="text-sm text-sky-800">
              Customising your box office can affect the accessibility of your
              listings and event pages for ticket buyers.{" "}
              <Link
                to="https://help.tickettailor.com/en/articles/9749431-how-to-make-your-ticket-tailor-events-accessible"
                className="underline text-sky-700 font-medium"
              >
                Read about accessibility
              </Link>
            </div>
          </div>
        </div> */}
        </div>
      </div>
      <div className="flex mt-7 gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-4 md:pr-6">
        <Button
          buttonType="submit"
          buttonLabel={t("Publish changes")}
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Form>
  );
};

export default OrganisationContent;
